import EstimateNumericFilter from './EstimateNumericFilter';
import Filters from 'components/framework/filters/Filters';
import ProjectFilter from 'components/framework/filters/ProjectFilter';
import RecordTypeFilter from 'components/framework/filters/RecordTypeFilter';
import ReleaseFilter from 'components/framework/filters/ReleaseFilter';
import SearchFilter from 'components/framework/filters/SearchFilter';
import TagFilter from 'components/framework/filters/TagFilter';
import UserFilter from 'components/framework/filters/UserFilter';
import WorkflowStatusFilter from 'components/framework/filters/WorkflowStatusFilter';
import { observable } from '@nx-js/observer-util';
import ItemFilter from './ItemFilter';
import { type FilterDefinition } from './components/Filters';
import { type WorkflowStatus } from '../models';
import { type RecordUnion } from 'components/framework/models/generated/types';

export const VISIBLE_FILTERS_KEY = 'visibleFilters';

interface DevelopBoardFiltersDefaults {
  teamWorkflowStatuses: WorkflowStatus[];
}

export type DevelopBoardFilterParams = {
  defaults: DevelopBoardFiltersDefaults;
  initialValues?: Record<string, any>;
  hasEpics?: boolean;
};

/**
 * Manages the collection of filter values for various Develop pages
 * record filters. Detects values from available records, checks
 * whether records match set filters, and returns filter definitions
 * in a way that the filter components can use.
 */
export default class DevelopBoardFilters extends Filters {
  searchDefinition: FilterDefinition;
  filters: {
    teamStatus: WorkflowStatusFilter;
    type: RecordTypeFilter;
    tags: TagFilter;
    project: ProjectFilter;
    release: ReleaseFilter;
    assignee: UserFilter;
    search: SearchFilter;
    initialEstimate: EstimateNumericFilter;
    originalEstimate: EstimateNumericFilter;
    visibleFilters: ItemFilter<any>;
  };

  constructor({
    defaults = { teamWorkflowStatuses: [] },
    initialValues = {},
    hasEpics = true,
  }: DevelopBoardFilterParams) {
    super();

    const availableTypes: ('Epic' | 'Feature' | 'Requirement')[] = [
      'Feature',
      'Requirement',
    ];

    if (hasEpics) {
      availableTypes.unshift('Epic');
    }

    this.defaults = {
      teamStatus: defaults.teamWorkflowStatuses
        .filter(s => !s.isCompleted)
        .map(s => s.id),
    };

    this.filters = observable({
      teamStatus: new WorkflowStatusFilter(
        [],
        defaults.teamWorkflowStatuses.map(s => [s.name, s.id]),
        {
          statusAttribute: 'teamWorkflowStatus',
        }
      ),
      type: new RecordTypeFilter({
        initialItems: [],
        recordTypes: availableTypes,
      }),
      tags: new TagFilter([]),
      project: new ProjectFilter([]),
      release: new ReleaseFilter([]),
      assignee: new UserFilter([]),
      search: new SearchFilter(),
      initialEstimate: new EstimateNumericFilter(
        { numeric_type: 'any' },
        'initialEstimate'
      ),
      originalEstimate: new EstimateNumericFilter(
        { numeric_type: 'any' },
        'originalEstimate'
      ),
      visibleFilters: new ItemFilter([]),
    });

    this.definitions = {
      teamStatus: {
        title: 'Team status: ',
        type: 'selectro',
        getOptions: () => Object.values(this.filters.teamStatus.allOptions),
      },
      type: {
        title: 'Record type: ',
        type: 'selectroV2',
        filterOptions: RecordTypeFilter.getSelectroProps(),
        getOptions: () => this.filters.type.selectroItems({ withAny: false }),
      },
      tags: {
        title: 'Tags: ',
        type: 'selectro',
        getOptions: () => Object.values(this.filters.tags.allOptions),
      },
      project: {
        title: 'Workspace: ',
        type: 'selectro',
        getOptions: () => Object.values(this.filters.project.allOptions),
      },
      release: {
        title: 'Release: ',
        type: 'selectro',
        getOptions: () => Object.values(this.filters.release.allOptions),
      },
      assignee: {
        title: 'Users: ',
        type: 'selectro',
        getOptions: () => Object.values(this.filters.assignee.allOptions),
      },
      initialEstimate: {
        title: 'Initial estimate',
        type: 'numeric',
      },
      originalEstimate: {
        title: 'Detailed estimate',
        type: 'numeric',
      },
    };

    this.searchDefinition = {
      title: 'Name, status, assignee, tag, or reference',
      type: 'text',
    };

    this.recordAdders = {
      assignedToUser: record => {
        this.filters.assignee.addOption({
          value: record.assignedToUser.id || '',
          text: record.assignedToUser.name,
        });
      },
      tags: record => {
        record.tags.forEach(tag => {
          this.filters.tags.addOption({ value: tag.id, text: tag.name });
        });
      },
      release: record => {
        this.filters.release.addOption({
          value: record.release.id,
          text: record.release.name,
        });
      },
      project: record => {
        this.filters.project.addOption({
          value: record.project.id,
          text: record.project.name,
        });
      },
      teamWorkflowStatus: record => {
        this.filters.teamStatus.addOption({
          value: record.teamWorkflowStatus.id,
          text: record.teamWorkflowStatus.name,
        });
      },
    };

    this.setDefaults(initialValues);
  }

  matches(record: RecordUnion): boolean {
    return Object.entries(this.filters).every(
      // Ignore (always pass) the visibleFilters filter since it's just used
      // for visibility on the filter bar in develop screens.
      ([name, f]) => name === VISIBLE_FILTERS_KEY || f.matches(record)
    );
  }
}
